<!--
  Copyright 2026 Tecnalia, Basque Research & Technology Alliance (BRTA)

  Licensed under the Apache License, Version 2.0 (the "License");
  you may not use this file except in compliance with the License.
  You may obtain a copy of the License at
 
     http://www.apache.org/licenses/LICENSE-2.0
 
  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.
 
   SPDX-License-Identifier: Apache-2.0
-->

<div class="edc-collapsible-json-view">
	<div
		class="edc-collapsible-json-view__toolbar"
		role="toolbar"
		[attr.aria-label]="'GENERAL.RAW-JSON-TOOLBAR-ARIA' | translate"
	>
		<div
			class="edc-collapsible-json-view__mode-group"
			role="group"
			[attr.aria-label]="'GENERAL.RAW-JSON-MODE-GROUP-ARIA' | translate"
		>
			<button
				type="button"
				class="edc-collapsible-json-view__mode-btn"
				[class.edc-collapsible-json-view__mode-btn--active]="viewMode() === 'full'"
				(click)="setViewMode('full')"
			>
				{{ 'GENERAL.RAW-JSON-MODE-FULL' | translate }}
			</button>
			<button
				type="button"
				class="edc-collapsible-json-view__mode-btn"
				[class.edc-collapsible-json-view__mode-btn--active]="viewMode() === 'structured'"
				(click)="setViewMode('structured')"
			>
				{{ 'GENERAL.RAW-JSON-MODE-STRUCTURED' | translate }}
			</button>
		</div>
		@if (viewMode() === 'structured' && sections().length > 1) {
			<div class="edc-collapsible-json-view__toolbar-icons" role="group">
				<button
					type="button"
					class="edc-collapsible-json-view__icon-btn"
					(click)="expandAll()"
					[attr.aria-label]="'GENERAL.RAW-JSON-EXPAND-ALL' | translate"
					[attr.title]="'GENERAL.RAW-JSON-EXPAND-ALL' | translate"
				>
					<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" class="bi bi-chevron-double-down" viewBox="0 0 16 16" aria-hidden="true">
						<path fill-rule="evenodd" d="M1.646 6.646a.5.5 0 0 1 .708 0L8 12.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708zm0-3a.5.5 0 0 1 .708 0L8 9.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
					</svg>
				</button>
				<button
					type="button"
					class="edc-collapsible-json-view__icon-btn"
					(click)="collapseAll()"
					[attr.aria-label]="'GENERAL.RAW-JSON-COLLAPSE-ALL' | translate"
					[attr.title]="'GENERAL.RAW-JSON-COLLAPSE-ALL' | translate"
				>
					<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" class="bi bi-chevron-double-up" viewBox="0 0 16 16" aria-hidden="true">
						<path fill-rule="evenodd" d="M7.646 2.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 3.707 2.354 9.354a.5.5 0 1 1-.708-.708zm0 4a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 7.707l-5.646 5.647a.5.5 0 0 1-.708-.708z"/>
					</svg>
				</button>
			</div>
		}
	</div>

	@if (viewMode() === 'full') {
		<div class="edc-collapsible-json-view__unified-card">
			<div class="edc-collapsible-json-view__unified-body edc-collapsible-json-view__unified-body--continuous">
				@if (fullDocumentText() === null) {
					<p class="edc-collapsible-json-view__empty-hint mb-0">
						{{ 'GENERAL.RAW-JSON-EMPTY-HINT' | translate }}
					</p>
				} @else {
					<pre><code class="json">{{ fullDocumentText() }}</code></pre>
				}
			</div>
		</div>
	} @else {
		<div class="edc-collapsible-json-view__sections edc-collapsible-json-view__sections--structured">
			@for (section of sections(); track section.trackId; let i = $index) {
				<details
					#sectionDetailsStructured
					class="edc-collapsible-json-view__section"
					[open]="initialOpenStructured(i)"
				>
					<summary class="edc-collapsible-json-view__summary">
						<span class="edc-collapsible-json-view__chevron" aria-hidden="true"></span>
						<span class="edc-collapsible-json-view__title">
							@switch (section.kind) {
								@case ('empty') {
									{{ 'GENERAL.RAW-JSON-EMPTY' | translate }}
								}
								@case ('whole') {
									{{ 'GENERAL.RAW-JSON-WHOLE' | translate }}
								}
								@case ('fallback') {
									{{ 'GENERAL.RAW-JSON-NOT-JSON' | translate }}
								}
								@default {
									{{ section.title }}
								}
							}
						</span>
						@if (section.lineCount > 0) {
							<span class="edc-collapsible-json-view__lines">
								{{ 'GENERAL.RAW-JSON-LINES-COUNT' | translate: { count: section.lineCount } }}
							</span>
						}
					</summary>
					<div class="edc-collapsible-json-view__body">
						@if (section.kind === 'empty') {
							<p class="edc-collapsible-json-view__empty-hint mb-0">{{ 'GENERAL.RAW-JSON-EMPTY-HINT' | translate }}</p>
						} @else {
							<pre><code class="json">{{ section.formatted }}</code></pre>
						}
					</div>
				</details>
			}
		</div>
	}
</div>